import { FINANCING_TYPE } from '../../api-v2/admin-credits'
import { type NonEnumVariableType, VARIABLE_SCOPE, type VariableMetadata } from '../variable'

/**
 * EL CATÁLOGO DEL BINDING DE CRÉDITOS: lo que un aviso sabe sin preguntarle a ninguna etapa.
 *
 * Dieciséis variables. Once salen de la NEGOCIACIÓN y de su acuerdo de pago —el cliente, el
 * inmueble y el asesor existen porque existe ella—, cuatro del EXPEDIENTE y del aviso que está
 * saliendo (`expediente.*`) y una del SISTEMA. Las
 * que PRODUCEN las etapas (`etapa2.amountToFinance`) no se listan acá: las deriva
 * `stage-variables.ts` de la propia plantilla, porque cambian cada vez que alguien edita el
 * proceso.
 *
 * ── ⭐ POR QUÉ APARECIÓ LA FAMILIA `expediente.*` ───────────────────────────
 *
 * Hasta la tanda 1 estos cuatro datos no eran variables: los inyectaba el MARCO del correo. La
 * ficha del expediente («Expediente: 41 / Negociación: N-… / Cliente: … / Inmueble: …») y el botón
 * «Ver expediente» los dibujaba una plantilla `.tsx` que el editor no podía tocar, así que el
 * enlace y el código vivían en el código y no en el catálogo.
 *
 * Con el marco vuelto CONTENIDO —la ficha es una tabla del catálogo, el botón es un botón—, esos
 * datos tienen que entrar por la única puerta que el autor tiene: una variable. Sin estas cuatro,
 * la plantilla «Ficha del expediente» no tendría con qué llenarse y el botón no tendría a dónde ir.
 *
 * Las claves van en español y son estables de por vida: se guardan dentro del texto de los avisos
 * (`{{ cliente.nombre }}`), así que renombrarlas rompe plantillas ya escritas. Las etiquetas, en
 * cambio, son traducción y se corrigen sin costo.
 *
 * ── EL SCOPE ES `credit_notice`, Y HOY ES LA VERDAD ──────────────────────────
 *
 * ⚠️ TODAS se resuelven cuando dispara un efecto (`noticeVariableValues` / `creditNoticeFacts`),
 * contra un `CreditEffectSnapshot` que carga la negociación en vivo. **Ninguna llega hoy a
 * `stageVisibilityFacts`**, y por eso el «Mostrar sólo si» de un campo no las puede mirar — no es
 * una omisión, es que sus valores viven en otras colecciones.
 *
 * `credito.modalidad` es la que va a estrenar `credit_instance` (ADR 0036, ola 2): ya es
 * `financingType`, un campo NATIVO del expediente, y los dos llamadores lo tienen en la mano. Pero
 * hasta que los hechos lo lleven, declararlo acá sería declarar una capacidad que no existe: el
 * scope es DECLARATIVO, no aspiracional.
 *
 * ⛔ Acá no hay valores. Resolverlos es de quien arma el aviso; el binding de la API
 * (`credits/domain/credit-binding.ts`) referencia estos metadatos y les pone la forma que el
 * kernel espera.
 */

/**
 * Los tipos que USA Créditos, angostados del vocabulario canónico.
 *
 * Es `VariableType` sin `enum` — y no por casualidad: acá el dominio cerrado SIEMPRE se declara
 * con `options`, que es la forma que el ADR 0036 §3 deja en pie. `enum` es la derivación que hace
 * el puente del editor, no un tipo que este catálogo escriba.
 *
 * ⭐ `url` entró con `expediente.enlace`: es el único tipo cuyo VALOR se hace cumplir en el correo
 * —`emailDocHref` resuelve el token y después exige `https:`— y por eso no se declara `string`.
 */
export type CreditVariableType = NonEnumVariableType

/** El metadato canónico, angostado a lo que este catálogo puede declarar. */
export type CreditVariableMetadata = Omit<VariableMetadata, 'type'> & {
  type: CreditVariableType
}

/** Agrupador del selector. En Créditos es texto libre: para las etapas es el nombre de la etapa. */
const CATEGORY = {
  NEGOTIATION: 'Negociación',
  CLIENT: 'Cliente',
  PROPERTY: 'Inmueble',
  PROJECT: 'Proyecto',
  ADVISOR: 'Asesor',
  CREDIT: 'Crédito',
  DOSSIER: 'Expediente',
  SYSTEM: 'Sistema'
} as const

/** Hoy el binding sólo llega al aviso. Ver el docblock de arriba antes de tocar esto. */
const NOTICE_ONLY = [VARIABLE_SCOPE.CREDIT_NOTICE] as const

export const CREDIT_BINDING_VARIABLE_METADATA: CreditVariableMetadata[] = [
  {
    key: 'negociacion.codigo',
    label: 'Código de la negociación',
    category: CATEGORY.NEGOTIATION,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'N-000000123',
    insertable: true
  },
  {
    key: 'cliente.nombre',
    label: 'Nombre del cliente',
    category: CATEGORY.CLIENT,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'María Fernanda López',
    insertable: true
  },
  {
    key: 'cliente.nombreFormal',
    label: 'Nombre formal del cliente',
    description: 'Nombre con mayúsculas y minúsculas naturales para saludos al cliente.',
    category: CATEGORY.CLIENT,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'María Fernanda de la Cruz',
    insertable: true
  },
  {
    key: 'cliente.email',
    label: 'Correo del cliente',
    category: CATEGORY.CLIENT,
    type: 'email',
    scopes: NOTICE_ONLY,
    example: 'cliente@ejemplo.com',
    insertable: true
  },
  {
    key: 'inmueble.nombre',
    label: 'Inmueble',
    category: CATEGORY.PROPERTY,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'Apartamento 502',
    insertable: true
  },
  {
    key: 'proyecto.nombre',
    label: 'Proyecto',
    category: CATEGORY.PROJECT,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'Cañada Santa Fe',
    insertable: true
  },
  {
    key: 'asesor.nombre',
    label: 'Asesor',
    category: CATEGORY.ADVISOR,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'Luis Herrera',
    insertable: true
  },
  {
    key: 'asesor.email',
    label: 'Correo del asesor',
    category: CATEGORY.ADVISOR,
    type: 'email',
    scopes: NOTICE_ONLY,
    example: 'asesor@ejemplo.com',
    insertable: true
  },
  {
    key: 'credito.modalidad',
    label: 'Modalidad de pago',
    category: CATEGORY.CREDIT,
    type: 'string',
    scopes: NOTICE_ONLY,
    // Dominio cerrado: el editor ofrece el selector, jamás un texto libre. Es la misma variable
    // sobre la que bifurca el proceso, así que un valor escrito a mano sería una ruta muerta.
    options: [
      { value: FINANCING_TYPE.CREDIT, label: 'Crédito bancario' },
      { value: FINANCING_TYPE.CASH, label: 'Contado' }
    ],
    insertable: true
  },
  {
    key: 'credito.fechaUltimoPago',
    label: 'Fecha del último pago',
    category: CATEGORY.CREDIT,
    type: 'date',
    scopes: NOTICE_ONLY,
    example: '2027-01-15',
    insertable: true
  },
  {
    key: 'credito.saldoAFinanciar',
    label: 'Saldo a financiar',
    category: CATEGORY.CREDIT,
    type: 'number',
    scopes: NOTICE_ONLY,
    example: 850000,
    insertable: true
  },
  {
    key: 'expediente.codigo',
    label: 'Código del expediente',
    category: CATEGORY.DOSSIER,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: '41',
    insertable: true
  },
  {
    key: 'expediente.etapa',
    label: 'Etapa del proceso',
    category: CATEGORY.DOSSIER,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'Inicio y contacto a cliente',
    insertable: true
  },
  {
    key: 'expediente.punto',
    label: 'Punto que disparó el aviso',
    category: CATEGORY.DOSSIER,
    type: 'string',
    scopes: NOTICE_ONLY,
    example: 'Aviso interno de inicio',
    insertable: true
  },
  {
    /**
     * ☠️ APUNTA A `/admin`, ASÍ QUE NO VA EN UN CORREO AL CLIENTE.
     *
     * El marco viejo resolvía esto solo: el formato `client` simplemente no recibía la URL. Ahora
     * la decisión es de quien redacta —poner o no el botón en la plantilla del cliente—, y por eso
     * la etiqueta lo dice. Un cliente que reciba este enlace se encuentra una puerta cerrada, y de
     * paso la forma de nuestras URLs internas.
     */
    key: 'expediente.enlace',
    label: 'Enlace al expediente (panel interno)',
    description:
      'Abre el expediente en el panel de administración. No lo pongas en un correo al cliente: no puede abrirlo.',
    category: CATEGORY.DOSSIER,
    type: 'url',
    scopes: NOTICE_ONLY,
    example: 'https://app.example.com/admin/creditos/crd_abc123',
    insertable: true
  },
  {
    key: 'sistema.anio',
    label: 'Año actual',
    category: CATEGORY.SYSTEM,
    type: 'number',
    scopes: NOTICE_ONLY,
    example: new Date().getFullYear(),
    insertable: true
  }
]
